import type { ProductStructureResponse, StepDto } from '@/types/api'

/**
 * Frontend-only visibility for assigned steps/modules.
 * A tool with `extra.assignedStudents` (non-empty) is visible only to the
 * listed profile ids; absent or empty means visible to everyone.
 */
export function isStepVisibleToStudent(extra: any, profileId: string): boolean {
  const assigned = extra?.assignedStudents
  if (!Array.isArray(assigned) || assigned.length === 0) return true
  return assigned.map(String).includes(profileId)
}

/**
 * Number of students a step/module is restricted to. Reads `extra` defensively
 * (it may arrive as an object or as a JSON string) and returns 0 when the item
 * is not restricted. Only the owner/team ever receives a non-empty list.
 */
export function getAssignedStudentsCount(extra: unknown): number {
  let parsed: unknown = extra
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return 0
    }
  }
  if (!parsed || typeof parsed !== 'object') return 0
  const assigned = (parsed as { assignedStudents?: unknown }).assignedStudents
  return Array.isArray(assigned) ? assigned.length : 0
}

type StepWithChildren = StepDto & { child_steps?: StepDto[] }

function filterSteps<T extends StepDto>(steps: T[] | undefined, profileId: string): T[] | undefined {
  if (!steps) return steps
  return steps
    .filter((step) => isStepVisibleToStudent(step.extra, profileId))
    .map((step) => {
      const children = (step as StepWithChildren).child_steps
      if (!children) return step
      return { ...step, child_steps: filterSteps(children, profileId) }
    })
}

/**
 * Filters every tool list of a product structure response for the logged
 * student. Applied at the service layer so all surfaces (content page,
 * module grid, kanban, certificate, navigation) stay consistent.
 */
export function filterStructureForStudent<T extends ProductStructureResponse>(
  response: T,
  profileId: string
): T {
  if (!profileId) return response

  // The product owner/team previewing the member area must keep seeing everything
  if (response.product?.viewer_is_owner_or_team) return response

  return {
    ...response,
    modules: response.modules
      ?.filter((module) => isStepVisibleToStudent(module.extra, profileId))
      .map((module) => ({
        ...module,
        steps: filterSteps(module.steps, profileId),
      })),
    steps: filterSteps(response.steps, profileId),
    independent_steps: filterSteps(response.independent_steps, profileId),
  }
}
